<template>
    <div class="flex h-full">
        <!-- Sidebar: plate type list -->
        <div class="bg-white/10 text-white w-56 min-w-48 h-full overflow-y-auto p-2 border-r border-white/20 flex flex-col">
            <div class="flex justify-between items-center mb-2 flex-shrink-0">
                <span class="font-semibold text-sm text-gray-300">Plate Types</span>
                <button @click="addPlateType"
                    class="bg-ui-active text-white px-2 py-1 rounded hover:opacity-90 text-xs">
                    Add
                </button>
            </div>
            <ul class="flex-1">
                <li v-for="plate in plateTypes" :key="plate.boardname"
                    :class="[
                        'py-2 px-2 rounded cursor-pointer mb-1 text-sm flex items-center justify-between',
                        selectedPlate && selectedPlate.boardname === plate.boardname
                            ? 'bg-white/20 text-white border border-white/30'
                            : 'text-gray-400 hover:text-white hover:bg-white/10 bg-white/5 border border-white/10',
                    ]"
                    @click="selectPlate(plate)">
                    <span class="flex-1 truncate">{{ plate.title || plate.boardname }}</span>
                    <button class="ml-2 text-red-400 hover:text-red-300"
                        title="Double-click to delete"
                        @dblclick.stop="deletePlateType(plate.boardname)"
                        @click.stop>
                        <font-awesome-icon icon="fa-solid fa-trash-can" />
                    </button>
                </li>
            </ul>
        </div>

        <!-- Details/Edit Panel -->
        <div class="flex-1 overflow-y-auto bg-transparent p-4">
            <div v-if="selectedPlate" class="flex flex-col gap-3 max-w-lg">
                <h2 class="font-semibold text-sm text-gray-300 mb-1">Edit Plate Type</h2>
                <form @submit.prevent="savePlateType" class="flex flex-col gap-3">

                    <div class="border border-white/20 rounded-md p-3 space-y-2 bg-white/10">
                        <p class="text-gray-300 font-semibold text-xs">Identity</p>
                        <div class="flex items-center gap-2">
                            <span class="w-2/5 shrink-0 text-xs text-gray-400">Board Name</span>
                            <input type="text" v-model="editPlate.boardname" :disabled="isExistingPlate"
                                class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed" />
                        </div>
                        <div class="flex items-center gap-2">
                            <span class="w-2/5 shrink-0 text-xs text-gray-400">Title</span>
                            <input type="text" v-model="editPlate.title"
                                class="flex-1 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-slate-400" />
                        </div>
                    </div>

                    <div class="border border-white/20 rounded-md p-3 space-y-2 bg-white/10">
                        <p class="text-gray-300 font-semibold text-xs">Screen</p>
                        <div class="flex gap-2">
                            <div class="flex-1">
                                <form-label class="block text-xs text-white mb-1">Width</form-label>
                                <input v-model.number="editPlate.screenSize.width" type="number"
                                    class="border border-white/20 rounded px-2 py-1 w-full bg-white/15 text-white text-xs" />
                            </div>
                            <div class="flex-1">
                                <form-label class="block text-xs text-white mb-1">Height</form-label>
                                <input v-model.number="editPlate.screenSize.height" type="number"
                                    class="border border-white/20 rounded px-2 py-1 w-full bg-white/15 text-white text-xs" />
                            </div>
                            <div class="flex-1">
                                <form-label class="block text-xs text-white mb-1">Shape</form-label>
                                <select v-model="editPlate.shape"
                                    class="border border-white/20 rounded px-2 py-1 w-full bg-white/15 text-white text-xs">
                                    <option value="rectangular">Rectangular</option>
                                    <option value="round">Round</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="border border-white/20 rounded-md p-3 space-y-2 bg-white/10">
                        <p class="text-gray-300 font-semibold text-xs">Hardware</p>
                        <div class="flex gap-2">
                            <div class="flex-1">
                                <form-label class="block text-xs text-white mb-1">RAM Size</form-label>
                                <select v-model.number="editPlate.ramSize"
                                    class="border border-white/20 rounded px-2 py-1 w-full bg-white/15 text-white text-xs">
                                    <option :value="128">128 KB</option>
                                    <option :value="320">320 KB</option>
                                    <option :value="400">400 KB</option>
                                    <option :value="512">512 KB</option>
                                    <option :value="1024">1024 KB</option>
                                    <option :value="2048">2048 KB</option>
                                </select>
                            </div>
                            <div class="flex-1">
                                <form-label class="block text-xs text-white mb-1">Flash Size</form-label>
                                <select v-model.number="editPlate.flashSize"
                                    class="border border-white/20 rounded px-2 py-1 w-full bg-white/15 text-white text-xs">
                                    <option :value="1024">1 MB</option>
                                    <option :value="2048">2 MB</option>
                                    <option :value="4096">4 MB</option>
                                    <option :value="8192">8 MB</option>
                                    <option :value="16384">16 MB</option>
                                </select>
                            </div>
                            <div class="flex-1">
                                <form-label class="block text-xs text-white mb-1">PS-RAM Size</form-label>
                                <select v-model.number="editPlate.psRamSize"
                                    class="border border-white/20 rounded px-2 py-1 w-full bg-white/15 text-white text-xs">
                                    <option :value="0">0 MB</option>
                                    <option :value="2048">2 MB</option>
                                    <option :value="4192">4 MB</option>
                                    <option :value="8192">8 MB</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div class="border border-white/20 rounded-md p-3 space-y-2 bg-white/10">
                        <p class="text-gray-300 font-semibold text-xs">Theme</p>
                        <div class="flex gap-2">
                            <div class="flex-1">
                                <form-label class="block text-xs text-white mb-1">UI Theme</form-label>
                                <select v-model="editPlate.uiTheme"
                                    class="border border-white/20 rounded px-2 py-1 w-full bg-white/15 text-white text-xs">
                                    <option v-for="(themeName, themeKey) in themeLookup" :key="themeKey" :value="themeKey">
                                        {{ themeName }}
                                    </option>
                                </select>
                            </div>
                            <div class="flex-1">
                                <form-label class="block text-xs text-white mb-1">Primary Color</form-label>
                                <input v-model="editPlate.primary_color" type="color"
                                    class="border border-white/20 rounded px-1 py-0.5 w-full bg-white/15 h-8" />
                            </div>
                            <div class="flex-1">
                                <form-label class="block text-xs text-white mb-1">Secondary Color</form-label>
                                <input v-model="editPlate.secondary_color" type="color"
                                    class="border border-white/20 rounded px-1 py-0.5 w-full bg-white/15 h-8" />
                            </div>
                        </div>
                    </div>

                    <button type="submit"
                        class="w-full bg-ui-active text-white px-4 py-2 rounded hover:opacity-90 text-sm transition-colors">
                        Save
                    </button>
                </form>
            </div>

            <div v-else class="text-gray-500 text-center mt-10 text-sm italic">
                Select a plate type to edit or add a new one.
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { themeLookup } from '../../../utils/lookupTables';
import {
    plateTypeStruct,
    getAllPlateTypes,
    addPlateType as dbAdd,
    updatePlateType,
    deletePlateType as dbDelete,
} from '../../../utils/plateTypeDB';

const plateTypes = ref([]);
const selectedPlate = ref(null);
const editPlate = reactive({ screenSize: { width: 0, height: 0 } });

const isExistingPlate = computed(() =>
    plateTypes.value.some((p) => p.boardname === editPlate.boardname)
);

async function loadPlateTypes() {
    plateTypes.value = await getAllPlateTypes();
}

function selectPlate(plate) {
    selectedPlate.value = plate;
    const copy = JSON.parse(JSON.stringify(plate));
    Object.assign(editPlate, copy);
    if (!editPlate.screenSize) editPlate.screenSize = { width: 0, height: 0 };
}

function addPlateType() {
    const newPlate = JSON.parse(JSON.stringify(plateTypeStruct));
    selectedPlate.value = newPlate;
    Object.assign(editPlate, newPlate);
    if (!editPlate.screenSize) editPlate.screenSize = { width: 0, height: 0 };
}

async function savePlateType() {
    if (!editPlate.boardname) return;
    const plainPlate = JSON.parse(JSON.stringify(editPlate));
    const exists = plateTypes.value.find((p) => p.boardname === plainPlate.boardname);
    if (exists) {
        await updatePlateType(plainPlate);
    } else {
        await dbAdd(plainPlate);
    }
    await loadPlateTypes();
    selectPlate(plainPlate);
}

async function deletePlateType(boardname) {
    const bn = boardname ?? editPlate.boardname;
    if (!bn) return;
    await dbDelete(bn);
    await loadPlateTypes();
    if (selectedPlate.value?.boardname === bn) selectedPlate.value = null;
}

onMounted(loadPlateTypes);
</script>
